'use client'

/* eslint-disable @next/next/no-img-element */

import Link from 'next/link'
import { getPoolAccentColor } from '@/lib/pool-theme'

interface LeagueMatchCardProps {
  match: {
    matchNumber: string
    competition?: string
    championshipSlug?: string
    giornata: number
    date: string
    time: string
    home: string
    away: string
    homeLogoSrc?: string | null
    awayLogoSrc?: string | null
    result: string | null
    sets: string | null
    played: boolean
    venue?: string | null
    referees?: string | null
    poolSlug?: string | null
    stagioneId?: string
    badgePath?: string | null
    /** Link già pronto (partite di **torneo**: il loro match center non è sotto
     *  `/campionati`). Se c'è, vince sulla URL costruita da girone + numero gara. */
    href?: string | null
    /** Al posto di `G{n}`/«Giornata n» quando la gara non ha una giornata di girone. */
    roundLabel?: string | null
  }
  variant?: 'hero' | 'compact'
  noLink?: boolean
  /**
   * Senza cornice propria: sfondo, bordo, raggio e ombra li mette la card del
   * chiamante. Serve all'elenco della pagina **Partite**, che ci affianca la
   * colonna allegati e apre il pannello degli allegati sotto la stessa card.
   */
  frameless?: boolean
  /**
   * Nasconde l'etichetta del campionato in alto a sinistra. Serve dove è
   * ridondante (la pagina girone ha già girone e campionato nel titolo).
   * Su mobile l'etichetta è comunque nascosta (`hidden sm:block`): la riga
   * serve ai parziali, che su una partita al 5º set sono cinque pillole.
   */
  hideCompetition?: boolean
}

function teamShortName(name: string) {
  return name
    .replace(/(A.S.D.|ASD|SSD|Srl|SRL|U.S.)/g, '')
    .replace(/VOLLEY/g, 'Volley')
    .trim()
}

function logoProxyUrl(src: string) {
  return `/api/logo?src=${encodeURIComponent(src)}`
}

function TeamLogo({ src, team, size = 48, cinematic = false }: { src?: string | null; team: string; size?: number; cinematic?: boolean }) {
  if (src) {
    return (
      <div className={cinematic ? 'relative p-2' : ''}>
        {cinematic ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-[8%] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 38%, rgba(255,255,255,0) 72%)',
            }}
          />
        ) : null}
        <img
          src={logoProxyUrl(src)}
          alt={team}
          width={size}
          height={size}
          loading={cinematic ? 'eager' : 'lazy'}
          decoding="async"
          className="relative z-10 rounded-full object-contain"
          style={cinematic ? {
            width: size,
            height: size,
            mixBlendMode: 'normal',
            filter: 'brightness(1.08) contrast(1.18) saturate(1.06)',
          } : { width: size, height: size }}
        />
      </div>
    )
  }
  // Fallback: initials circle
  const initials = team
    .replace(/(A.S.D.|ASD|SSD|Srl|SRL|U.S.)/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
  return (
    <div
      className="flex items-center justify-center rounded-full bg-white/15 font-display font-bold tracking-wide"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  )
}

export function LeagueMatchCard({ match, variant = 'hero', noLink = false, frameless = false, hideCompetition = false }: LeagueMatchCardProps) {
  if (variant === 'compact') {
    const isPlayed = match.played && match.result
    const poolAccentColor = match.poolSlug ? getPoolAccentColor(match.poolSlug) : null
    const matchHref = match.href
      ?? (match.championshipSlug && match.poolSlug
        ? `/campionati/${match.championshipSlug}/${match.poolSlug}/${match.matchNumber}${match.stagioneId ? `?stagione=${match.stagioneId}` : ''}`
        : null)
    const card = (
      <article
        className={`league-watermark group relative overflow-hidden p-4 ${
          frameless ? '' : 'league-card rounded-2xl transition-shadow hover:shadow-[var(--league-shadow-md)]'
        }`}
        style={{
          ...(poolAccentColor ? { borderColor: poolAccentColor } : {}),
          ['--wm' as string]: match.badgePath ? `url(${match.badgePath})` : 'none',
        }}
      >
        {/* Top row: campionato (da `sm` in su) + parziali o giornata. Su mobile
            la riga è tutta dei parziali: con l'etichetta accanto le cinque
            pillole di una partita al 5º set andavano a capo. */}
        <div className="relative z-10 flex items-start justify-between gap-3">
          {!hideCompetition && (
            <span className="hidden min-w-0 truncate text-[12px] font-medium uppercase tracking-[0.14em] text-[color:var(--league-muted)] sm:block">
              {match.competition ?? 'Calendario lega'}
            </span>
          )}
          <div className="ml-auto flex min-w-0 items-start justify-end">
            {isPlayed && match.sets ? (
              // I parziali vanno a capo invece di essere tagliati: una partita al
              // 5º set (5 pillole) su mobile non ci sta accanto al titolo, e con
              // `overflow-hidden` le ultime sparivano (se ne vedevano 3).
              <div className="flex min-w-0 flex-wrap justify-end gap-1">
                {match.sets.split(' ').map((set, i) => (
                  <span
                    key={i}
                    className="shrink-0 rounded-full bg-[color:var(--league-surface-alt)] px-2 py-0.5 text-[12px] font-medium tabular-nums text-[color:var(--league-muted)]"
                  >
                    {set}
                  </span>
                ))}
              </div>
            ) : isPlayed ? (
              <span className="league-badge shrink-0 league-status-played">{match.result}</span>
            ) : (
              <span className="shrink-0 rounded-full bg-[color:var(--league-accent-soft)] px-2 py-0.5 text-[12px] font-medium text-[color:var(--league-accent-strong)]">
                {match.roundLabel ?? `G${match.giornata}`}
              </span>
            )}
          </div>
        </div>

        {/* Teams */}
        <div className="relative z-10 mt-3 flex items-stretch gap-3">
          {/* Left: logos + names */}
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <TeamLogo src={match.homeLogoSrc} team={match.home} size={28} />
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[color:var(--league-ink)]">
                {teamShortName(match.home)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <TeamLogo src={match.awayLogoSrc} team={match.away} size={28} />
              <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[color:var(--league-ink)]">
                {teamShortName(match.away)}
              </span>
            </div>
          </div>

          {/* Right: scores (played) or date+time block (upcoming) */}
          {isPlayed ? (
            <div className="flex flex-col justify-around">
              <span className="text-[20px] font-bold tabular-nums text-[color:var(--league-ink)]">
                {match.result!.split('-')[0]}
              </span>
              <span className="text-[20px] font-bold tabular-nums text-[color:var(--league-ink)]">
                {match.result!.split('-')[1]}
              </span>
            </div>
          ) : (
            <div className="flex shrink-0 flex-col items-center justify-center rounded-xl bg-[color:var(--league-surface-alt)] px-3 py-2 text-center">
              <span className="text-[20px] font-bold tabular-nums leading-none text-[color:var(--league-ink)]">
                {match.time || '—'}
              </span>
              <span className="mt-1 text-[12px] font-medium tabular-nums text-[color:var(--league-muted)]">{match.date}</span>
            </div>
          )}
        </div>

        {/* Footer: data/ora + palazzetto — arbitri */}
        <div className="relative z-10 mt-3 border-t border-[color:var(--league-line)] pt-3 text-[12px] text-[color:var(--league-muted)]">
          {/* Row 1: data · ora */}
          <div className="flex items-center gap-1.5">
            <span>{match.date}</span>
            <span className="opacity-40">·</span>
            <span>{match.time || 'TBD'}</span>
          </div>
          {/* Row 2: venue + arbitri (se presenti) */}
          {(match.venue || match.referees) && (
            <div className="mt-1 flex min-w-0 items-center gap-2 overflow-hidden">
              {match.venue && (
                <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                  <svg className="h-3 w-3 shrink-0 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  <span className="truncate">{match.venue.split(',')[0]}</span>
                </span>
              )}
              {match.referees && (
                <span className="flex shrink-0 items-center gap-1 overflow-hidden">
                  <svg className="h-3 w-3 shrink-0 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  <span className="max-w-[120px] truncate">{match.referees}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </article>
    )
    return matchHref && !noLink ? (
      <Link href={matchHref} className="block min-w-0 transition-colors hover:opacity-90">
        {card}
      </Link>
    ) : card
  }

  // ── HERO VARIANT ─────────────────────────────────────────────────────
  // Header del match center: una sola scatola che contiene TUTTO il
  // contesto della gara (competizione, stato, squadre, punteggio, data,
  // palestra, arbitri, numero gara). La pagina non deve ripetere queste
  // informazioni in una strip separata.
  // Il punteggio è già al centro dell'hero: il badge dice solo lo STATO
  // della gara, senza ripetere il risultato.
  const statusLabel = match.played ? 'Terminata' : (match.roundLabel ?? `Giornata ${match.giornata}`)

  return (
    <article
      className="league-hero league-watermark-hero relative h-full overflow-hidden rounded-[24px] md:rounded-[32px]"
      style={{ ['--wm' as string]: match.badgePath ? `url(${match.badgePath})` : 'none' }}
    >
      <div className="league-hero-inner relative z-10 px-5 py-5 md:px-8 md:py-8">

        {/* Top bar: competizione (contesto) + stato della gara */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {/* Su mobile l'etichetta va a capo invece di essere tagliata con i
              puntini: «Campionato Regionale 2° Livello Serie D Maschile Gir. A»
              non sta in una riga da 368 px e si leggeva tagliata a metà parola.
              Da `sm` in su resta su una riga con ellissi (riga compatta). */}
          <span className="min-w-0 text-[12px] font-semibold uppercase leading-tight tracking-[0.16em] text-white/65 sm:truncate md:text-[13px]">
            {match.competition ?? 'Calendario lega'}
          </span>
          <span className={`league-badge shrink-0 text-[12px] ${match.played ? 'bg-emerald-400/15 text-emerald-300' : 'bg-amber-400/15 text-amber-300'}`}>
            {statusLabel}
          </span>
        </div>

        {/* Main matchup — horizontal on all sizes.
            Le colonne delle squadre sono `minmax(0, 1fr)`: con la `1fr` secca il
            minimo era il **min-content** (logo 48 px + nome più lungo), quindi la
            riga non scendeva sotto ~380 px e su un telefono da 360-440 px spingeva
            la colonna ospiti fuori dalla card (nome e logo tagliati dal bordo).
            Ora le colonne si restringono e i nomi vanno a capo invece di uscire. */}
        <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 md:mt-7 md:gap-8">

          {/* Home team */}
          <div className="flex min-w-0 flex-col items-center gap-2 md:items-end md:gap-3">
            <TeamLogo src={match.homeLogoSrc} team={match.home} size={48} cinematic />
            <div className="min-w-0 text-center md:text-right">
              <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/40">Casa</div>
              <div className="mt-0.5 break-words font-display text-[17px] font-bold leading-tight tracking-[-0.02em] md:text-[32px]">
                {teamShortName(match.home)}
              </div>
            </div>
          </div>

          {/* Score center */}
          <div className="flex flex-col items-center gap-2">
            <div className="font-display text-[34px] font-bold leading-none tracking-[-0.04em] md:text-[56px]">
              {match.result ?? 'VS'}
            </div>
            {match.sets && (
              <div className="rounded-full bg-white/10 px-2.5 py-1 text-[12px] text-white/60 md:px-4 md:py-1.5">
                {match.sets}
              </div>
            )}
          </div>

          {/* Away team */}
          <div className="flex min-w-0 flex-col items-center gap-2 md:items-start md:gap-3">
            <TeamLogo src={match.awayLogoSrc} team={match.away} size={48} cinematic />
            <div className="min-w-0 text-center md:text-left">
              <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/40">Ospiti</div>
              <div className="mt-0.5 break-words font-display text-[17px] font-bold leading-tight tracking-[-0.02em] md:text-[32px]">
                {teamShortName(match.away)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom info strip — unica sede dei metadati della gara */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 border-t border-white/10 pt-4 text-[12px] text-white/60 md:mt-7 md:gap-x-4 md:text-[13px]">
          <span className="flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5 shrink-0 opacity-60 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            {match.date}
          </span>
          <span className="flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5 shrink-0 opacity-60 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {match.time || 'TBD'}
          </span>
          {match.venue && (
            <>
              <span className="opacity-30">·</span>
              <span className="flex min-w-0 items-center gap-1.5">
                <svg className="h-3.5 w-3.5 shrink-0 opacity-60 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                {/* Su telefono è il **palazzetto** e basta (stessa regola della
                    card compatta): l'indirizzo completo non ci sta in una riga e
                    finiva tagliato a metà («…MODUGNO BA, Via P»). Da `sm` in su
                    torna l'indirizzo intero, con ellissi se serve. */}
                  <span className="truncate">
                    <span className="sm:hidden">{match.venue.split(',')[0]}</span>
                    <span className="hidden sm:inline">{match.venue}</span>
                  </span>
              </span>
            </>
          )}
          {match.referees && (
            <>
              <span className="opacity-30">·</span>
              <span className="flex min-w-0 items-center gap-1.5">
                <svg className="h-3.5 w-3.5 shrink-0 opacity-60 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                <span className="truncate">{match.referees}</span>
              </span>
            </>
          )}
          <span className="opacity-30">·</span>
          {match.matchNumber ? <span className="shrink-0 tabular-nums">Match #{match.matchNumber}</span> : null}
        </div>
      </div>
    </article>
  )
}
